import { calculatePlaySlipPrices } from '@lottocom/price-calculation'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config, PriceList } from '../@types/editor'

import { useEditorOptionsStore } from '../stores/options'
import { useEditorPlayStore } from './editorPlayStore'

type PricedPlay = {
  fields: { name: string; selected: number[] }[]
  quickPick?: string
  perPlayAddOn?: Maybe<{ name: string; value: string }>
  id: string
  stake?: Maybe<string>
}

type PriceStore = {
  getIgnoredFieldNames(config: Config, play: PricedPlay): string[]
  isPlayEmpty(play: PricedPlay): boolean
  isPlayFlagged(config: Config, play: PricedPlay): boolean
  plays: PricedPlay[]
}

const SINGLE_DRAW_MULTIPLIER = 1

export const useEditorPrice = (lotteryId: LotteryId) => {
  const licensedTerritory = useLicensedTerritory({ raw: true })

  const store: PriceStore = useEditorPlayStore(lotteryId)

  const optionsStore = useEditorOptionsStore(lotteryId)

  const { result } = useLotteryQuery({ licensedTerritory, lotteryId })

  const config = computed<Maybe<Config>>(() => result.value?.lottery.currentConfig ?? null)

  const priceList = computed<Maybe<PriceList>>(() => result.value?.lottery.priceList ?? null)

  const startedPlays = computed(() => store.plays.filter((play) => !store.isPlayEmpty(play)))

  const isFlagged = (play: PricedPlay) => !config.value || store.isPlayFlagged(config.value, play)

  const pricedPlays = computed(() => startedPlays.value.filter((play) => !isFlagged(play)))

  const flaggedPlays = computed(() => startedPlays.value.filter(isFlagged))

  const playCount = computed(() => startedPlays.value.length)

  const flaggedCount = computed(() => flaggedPlays.value.length)

  const selectedAddOns = computed(() => optionsStore.selectedAddOns.filter(({ value }) => !!value))

  const numberOfDraws = computed(
    () =>
      (config.value?.numberOfDraws[0] ?? 0) *
      (optionsStore.prepaidSubscriptionAmount?.value || SINGLE_DRAW_MULTIPLIER),
  )

  const toPlayInput = (currentConfig: Config, play: PricedPlay) => {
    const ignoredFieldNames = store.getIgnoredFieldNames(currentConfig, play)

    return {
      addOns: play.perPlayAddOn?.value ? [play.perPlayAddOn] : [],
      // the backend wants every configured field, an ignored one is sent without numbers
      fields: play.fields.map(({ name, selected }) => ({
        name,
        selected: ignoredFieldNames.includes(name) ? [] : selected,
      })),
      stake: { price: play.stake ?? currentConfig.stakes[0].price },
    }
  }

  const playInputs = computed(() => {
    const currentConfig = config.value
    if (!currentConfig) return []
    return pricedPlays.value.map((play) => toPlayInput(currentConfig, play))
  })

  const currentPrice = computed(() => {
    const currentConfig = config.value
    const currentPriceList = priceList.value

    if (!currentConfig || !currentPriceList) return null

    try {
      return calculatePlaySlipPrices(
        {
          addOns: selectedAddOns.value,
          configVersion: currentConfig.version,
          lotteryId,
          numberOfDraws: numberOfDraws.value,
          plays: playInputs.value,
        },
        { ...currentPriceList, currency: 'USD' },
      )
    } catch {
      return null
    }
  })

  return {
    config,
    currentPrice,
    flaggedCount,
    flaggedPlays,
    playCount,
    playInputs,
    priceList,
    pricedPlays,
    selectedAddOns,
  }
}
